/**
 * Firmware download store (UpdateFirmware).
 *
 * Unlike the synthetic-only diagnostics/log uploads, UpdateFirmware now really
 * fetches the firmware image from the CSMS-supplied `location`, so a tester can
 * confirm the binary is reachable and verify it (size + SHA-256) and save it to
 * disk. The real fetch is browser-bounded: a cross-origin firmware host without
 * CORS headers fails → the lifecycle reports DownloadFailed (a useful signal).
 *
 * The downloaded bytes are held OUTSIDE reactive state (a module ref) — they can
 * be large and no component should subscribe to them. The reactive slice carries
 * only metadata; `saveToDisk` reads the held blob.
 */
import { create } from 'zustand';

export type FirmwareStatus =
  | 'idle'
  | 'downloading'
  | 'downloaded'
  | 'failed'
  | 'installing'
  | 'installed';

export interface FirmwareDownloadState {
  readonly status: FirmwareStatus;
  readonly url: string | null;
  readonly filename: string | null;
  readonly sizeBytes: number | null;
  readonly contentType: string | null;
  readonly sha256: string | null;
  readonly error: string | null;
  readonly at: number | null;
}

export interface FirmwareStoreActions {
  /** Begin a new download — clears any previously held image. */
  startDownload(url: string): void;
  setDownloaded(meta: {
    readonly filename: string;
    readonly sizeBytes: number;
    readonly contentType: string;
    readonly sha256: string;
    readonly blob: Blob;
  }): void;
  setFailed(error: string): void;
  setInstalling(): void;
  setInstalled(): void;
  /** Trigger a browser save of the held image (no-op outside the browser). */
  saveToDisk(): void;
  clear(): void;
}

export type FirmwareStore = FirmwareDownloadState & FirmwareStoreActions;

// Held outside reactive state — potentially large, never a render input.
let heldBlob: Blob | null = null;

const initial: FirmwareDownloadState = {
  status: 'idle',
  url: null,
  filename: null,
  sizeBytes: null,
  contentType: null,
  sha256: null,
  error: null,
  at: null,
};

export const useFirmwareStore = create<FirmwareStore>((set, get) => ({
  ...initial,

  startDownload(url): void {
    heldBlob = null;
    set({ ...initial, status: 'downloading', url, at: Date.now() });
  },

  setDownloaded(meta): void {
    heldBlob = meta.blob;
    set({
      status: 'downloaded',
      filename: meta.filename,
      sizeBytes: meta.sizeBytes,
      contentType: meta.contentType,
      sha256: meta.sha256,
      error: null,
      at: Date.now(),
    });
  },

  setFailed(error): void {
    heldBlob = null;
    set({ status: 'failed', error, at: Date.now() });
  },

  setInstalling(): void {
    set({ status: 'installing' });
  },

  setInstalled(): void {
    set({ status: 'installed' });
  },

  saveToDisk(): void {
    if (heldBlob === null || typeof document === 'undefined') return;
    const { filename } = get();
    const url = URL.createObjectURL(heldBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename ?? 'firmware.bin';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  clear(): void {
    heldBlob = null;
    set(initial);
  },
}));

/** Whether a downloaded image is currently held in memory (non-reactive). */
export function hasHeldFirmware(): boolean {
  return heldBlob !== null;
}
